import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, BellOff, Smartphone, CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { pushSupport, enablePush, disablePush, currentSubscription, isIOS } from "@/lib/push";

const LABELS = {
  subscriber: [["live", "Creators go live"], ["content", "New videos, photos & posts"], ["announcements", "Creator & platform announcements"], ["membership", "Membership renewals & expiry"], ["payments", "Payment results"], ["account", "Account & security alerts"]],
  creator: [["followers", "New followers"], ["subscribers", "New subscribers & cancellations"], ["tips", "Tips"], ["purchases", "Pay-per-view purchases"], ["moderation", "Moderation notices"], ["payouts", "Payout updates"], ["announcements", "Platform announcements"], ["account", "Account & security alerts"]],
};

function Row({ label, hint, checked, onChange, testid, disabled }) {
  return (
    <label className="flex min-h-[48px] items-center justify-between gap-4 border-t border-white/5 py-2 text-sm first:border-0">
      <span>{label}{hint && <span className="block text-[0.65rem] text-[#a7a0af]">{hint}</span>}</span>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} data-testid={testid} aria-label={label} />
    </label>
  );
}

function permissionText() {
  if (typeof Notification === "undefined") return "Not available in this browser";
  return { granted: "Allowed", denied: "Blocked in device settings", default: "Not asked yet" }[Notification.permission];
}

export function NotificationSettings({ audience = "subscriber" }) {
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState(false);
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState("idle");
  const support = pushSupport();
  const load = () => api.get("/push/config").then((r) => setCfg(r.data)).catch(() => setErr(true));
  useEffect(() => {
    load();
    currentSubscription().then((s) => setOn(!!s && Notification.permission === "granted")).catch(() => {});
  }, []);
  const savePrefs = async (prefs) => {
    const prev = cfg.prefs;
    setCfg({ ...cfg, prefs });
    setSaved("saving");
    try {
      const { data } = await api.put("/push/prefs", prefs);
      setCfg((c) => ({ ...c, prefs: data.prefs }));
      setSaved("saved");
    } catch {
      setCfg((c) => ({ ...c, prefs: prev }));
      setSaved("idle");
      toast.error("Couldn't save — your previous setting was kept.");
    }
  };
  const toggle = async (v) => {
    setBusy(true);
    try {
      if (v) { await enablePush(cfg.public_key); toast.success("This device is registered for notifications"); }
      else { await disablePush(); toast.success("This device was removed from notifications"); }
      setOn(v);
      load();
    } catch (e) {
      const m = e.message || "";
      toast.error(/denied|permission|blocked/i.test(m) ? "Notifications are blocked for this browser. Allow them in your browser or device settings (private/incognito windows can't receive notifications), then try again." : m || "Couldn't change notifications");
    } finally { setBusy(false); }
  };
  const test = async () => { try { await api.post("/push/test"); toast.success("Test sent — check this device's notifications"); } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); } };
  if (err) return <p data-testid="notification-settings-error" className="mt-6 text-sm text-red-300">Couldn't load notification settings. Please reload.</p>;
  if (!cfg) return <p className="mt-6 text-sm text-[#a7a0af]">Loading notification settings…</p>;
  const p = cfg.prefs;
  const set = (k) => (v) => savePrefs({ ...p, [k]: v });
  return (
    <section data-testid="notification-settings" className="mt-6 rounded-2xl glass p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold"><BellRing size={16} className="text-[#d42be8]" /> Notification preferences</h2>
        <span data-testid="push-save-state" className="flex items-center gap-1 text-[0.7rem] text-[#a7a0af]">
          {saved === "saving" && <><Loader2 size={12} className="animate-spin" /> Saving…</>}
          {saved === "saved" && <><CheckCircle2 size={12} className="text-emerald-300" /> Saved to your account</>}
        </span>
      </div>
      <p className="mt-1 text-[0.7rem] text-[#a7a0af]">Preferences save automatically to your account and apply on every device you sign in on.</p>

      <h3 className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#c77dff]">This device</h3>
      <p data-testid="push-device-status" className="mt-1 text-xs text-[#c7c1cf]">Permission: {permissionText()} · Registered devices on your account: {cfg.devices}</p>
      {support !== "ios-needs-install" && <p data-testid="push-iphone-note" className="mt-2 text-[0.7rem] text-[#a7a0af]">On iPhone, notifications only arrive in the installed app: Safari → Share → Add to Home Screen (iOS 16.4+), then open PEEPING from the icon and turn this device on.</p>}
      {support === "ios-needs-install" && (
        <p data-testid="push-ios-install" className="mt-2 flex gap-2 rounded-xl border border-[#a03cff]/40 bg-[#3a0d6e]/30 p-3 text-xs text-[#e0c2ff]"><Smartphone size={14} className="shrink-0" /> iPhone only delivers notifications to the installed app: in Safari tap Share → Add to Home Screen (iOS 16.4+), open PEEPING from the Home Screen icon, sign in, then turn this device on here. Your preferences below still save now.</p>
      )}
      {support === "unsupported" && <p data-testid="push-unsupported" className="mt-2 text-xs text-[#a7a0af]">This browser doesn't support web push notifications. Preferences still save for your other devices.</p>}
      {support === "supported" && (
        <div className="mt-1">
          <Row label="Notifications on this device" hint={on ? "Registered — this device receives PEEPING notifications." : "Off — tap to allow and register this device."} checked={on} disabled={busy || !cfg.public_key} onChange={toggle} testid="push-toggle" />
          {on && <button onClick={test} data-testid="push-test" className="mt-1 rounded-full border border-white/15 px-3 py-1.5 text-xs">Send test notification</button>}
          {typeof Notification !== "undefined" && Notification.permission === "denied" && <p className="mt-2 flex items-center gap-1 text-xs text-amber-200"><BellOff size={12} /> Blocked in device settings — allow notifications for PEEPING there first.</p>}
        </div>
      )}

      <h3 className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[#c77dff]">How notifications look</h3>
      <div className="mt-1">
        <Row label="Sound" hint="Plays your device's notification sound. Silent mode and Focus still apply." checked={p.sound !== false} onChange={set("sound")} testid="push-sound" />
        <Row label="Vibrate" hint={isIOS() ? "iPhone controls vibration itself; this applies on Android." : "Where the device supports it."} checked={p.vibrate} onChange={set("vibrate")} testid="push-vibrate" />
        <Row label="Discreet previews" hint="Lock screen shows only “PEEPING — You have a new update.”" checked={p.discreet} onChange={set("discreet")} testid="push-discreet" />
      </div>

      <h3 className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[#c77dff]">Notify me about</h3>
      <div className="mt-1">
        {LABELS[audience].map(([k, label]) => (
          <Row key={k} label={label} checked={p.categories[k] !== false} onChange={(v) => savePrefs({ ...p, categories: { ...p.categories, [k]: v } })} testid={`push-cat-${k}`} />
        ))}
      </div>
    </section>
  );
}
